
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import {
  MypageRecipeClip,
  MypageRecipeClips,
  MypageDialogInfo,
  HTMLDivElementEvent
} from "@/types/mypage";
import {
  isEndOfScrollBottom,
  createFavoriteRecipesToCommonRecipes
} from "@/utils/favorites-util";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import MyListFolderOperation from "@/components/favorites/MyListFolderOperation.vue";
import FavoritesFolderNameInputDialog from "@/components/favorites/FavoritesFolderNameInputDialog.vue";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue"; 

export default defineComponent({
  name: "MypageFolderPage",
  components: {
    MyListFolderOperation,
    FavoritesFolderNameInputDialog,
    CommonRecipeList,
    CommonDesignDialog
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // ログIDの設定
    setLogId(PAGE_ID.FAVORITES_MYLIST_FOLDER);

    // レシピ表示は、削除ボタン付き
    const deleteMode = 1;
    NativeClient.sendDebugLog("MypageFolderPage setupstart");
    CommonUtil.scroll({ top: 0, left: 0 });

    // テキスト
    const operationAppendText = "レシピを追加する";
    const folderDeleteMessage = "フォルダを\n削除してよろしいですか？";
    const recipeDeleteMessage = "お気に入りのリストから\n削除してよろしいですか？";
    const dialogMessageForRecipeLimitOver = "レシピは99個までしか登録する\nことが出来ません。";
    const dialogMessageCompleteFolderDelete = "フォルダを削除しました";
    const dialogMessageForCompleteRecipeDelete = "レシピを削除しました";
    const dialogMessageForLimitOverFolderName = COMMON_MESSAGE_STRING.FAVORITES_FOLDER_NAME_LIMIT_OVER;
    const okText = "OK";
    const cancelText = "キャンセル";
    const closeText = "閉じる";

    // 汎用ダイアログ
    const isShowDialog = ref(false);
    const generalDialogInfo = ref(new MypageDialogInfo());
    const closeGeneralDialog = () => {
      NativeClient.sendDebugLog("MypageFolderPage closeGeneralDialog()");
      isShowDialog.value = false;
    };
    const negativeHandleGeneralDialog = () => {
      closeGeneralDialog();
      if (generalDialogInfo.value.negativeCallback != null) {
        generalDialogInfo.value.negativeCallback();
      }
    };
    const positiveHandleGeneralDialog = () => {
      closeGeneralDialog();
      if (generalDialogInfo.value.positiveCallback != null) {
        generalDialogInfo.value.positiveCallback();
      }
    };

    // 操作完了のダイアログ表示
    const showCompleteOperationDialog = (title: string, message: string, completeHandler: (() => void) | null) => {
      const dialogInfo = new MypageDialogInfo();
      dialogInfo.type = 13;
      dialogInfo.title = title;
      dialogInfo.message = message;
      dialogInfo.positiveButtonLabel = "";
      dialogInfo.negativeButtonLabel = closeText;
      dialogInfo.negativeCallback = completeHandler;
      generalDialogInfo.value = dialogInfo
      isShowDialog.value = true;
    };

    // 操作ミス通知ダイアログ
    const showOperationErrorDialog = (title: string, message: string) => {
      const dialogInfo = new MypageDialogInfo();
      dialogInfo.type = 9;
      dialogInfo.title = title;
      dialogInfo.message = message;
      dialogInfo.positiveButtonLabel = "";
      dialogInfo.negativeButtonLabel = okText;
      generalDialogInfo.value = dialogInfo
      isShowDialog.value = true;
    };

    // 操作確認（キャンセル／OK）ダイアログ
    const showConfirmOperationDialog = (title: string, message: string, positiveHandler: (() => void)) => {
      const dialogInfo = new MypageDialogInfo();
      dialogInfo.type = 6;
      dialogInfo.title = title;
      dialogInfo.message = message;
      dialogInfo.positiveButtonLabel = okText;
      dialogInfo.negativeButtonLabel = cancelText;
      dialogInfo.positiveCallback = positiveHandler;
      generalDialogInfo.value = dialogInfo
      isShowDialog.value = true;
    };

    // WebAPIのリクエスト状況
    class MypageFolderPageRequestType {
      type: number; // 0:なし、1:リスト、2:フォルダ削除、3:フォルダ名変更、4:レシピ削除、 5:追加読み込み
      folderName: string; // 変更後のフォルダ名
      recipeId: number; // 削除対象のレシピ
      constructor(type: number, folderName: string, recipeId: number) {
        this.type = type;
        this.folderName = folderName;
        this.recipeId = recipeId;
      }
    }
    let currentRequest = new MypageFolderPageRequestType(0, "", 0);

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      NativeClient.sendDebugLog("MypageFolderPage updateDisplay");
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // フォルダ名入力のためのリアクティブ
    const updateInputFolderNameKey = ref(0);
    const updateInputFolderName = () => {
      if (updateInputFolderNameKey.value == 100) {
        updateInputFolderNameKey.value = 0;
      } else {
        updateInputFolderNameKey.value++;
      }
    };

    // urlから選択されたフォルダーIdを取得(folderIdが指定されていなければ、「最近追加したもの」)
    let currentId = 0;
    let isRecentList = false;
    let currentFolderName = "";
    if (router.currentRoute.value.query.folderId == null) {
      isRecentList = true;
      currentFolderName = "お気に入りレシピ";
    } else {
      currentId = Number(router.currentRoute.value.query.folderId);
      const folderName = store.getters["mypage/getFolderName"];
      if (folderName == null) {
        NativeClient.sendDebugLog("MypageFolderPage folderName not exist");
        currentFolderName = "";
      } else {
        NativeClient.sendDebugLog("MypageFolderPage folderName exist = " + folderName);
        currentFolderName = folderName;
      }
    }
    const refCurrentFolderName = ref(currentFolderName);
    NativeClient.sendDebugLog("MypageFolderPage currentFolderName = " + currentFolderName);

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, currentFolderName));

    // 一覧クリア
    const clearList = () => {
      if (isRecentList) {
        // マイリスト
        store.dispatch("mypage/updateFavoriteRecipeList", null);
      } else {
        // フォルダのレシピ一覧
        store.dispatch("mypage/updateFolderRecipeList", null);
      }
    };

    //
    // API実行
    //
    let totalCount = 0;
    let recipeCount = 0;
    const getRecipeList = async () => {
      if (isRecentList) {
        // マイリスト
        NativeClient.sendDebugLog("MypageFolderPage getFavoriteRecipeList");
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        if (isValidApiAccessToken()) {
          currentRequest = new MypageFolderPageRequestType(1, "", 0);
          await store.dispatch("mypage/getFavoriteRecipeList");
          NativeClient.sendDebugLog("MypageFolderPage getRecipeList getFavoriteRecipeList end");
          const result: MypageRecipeClips = store.getters["mypage/getFavoriteRecipeList"];
          totalCount = result.totalCount;
          recipeCount = result.clips.length;
          updateDisplay();
        }
      } else {
        // フォルダのレシピ一覧
        NativeClient.sendDebugLog("MypageFolderPage getRecipeList getFolderRecipeList");
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        if (isValidApiAccessToken()) {
          currentRequest = new MypageFolderPageRequestType(1, "", 0);
          await store.dispatch("mypage/getFolderRecipeList", currentId);
          NativeClient.sendDebugLog("MypageFolderPage getFolderRecipeList end");
          const result: MypageRecipeClips = store.getters["mypage/getFolderRecipeList"];
          totalCount = result.totalCount;
          recipeCount = result.clips.length;
          updateDisplay();
        }
      }
      // リクエスト完了
      currentRequest = new MypageFolderPageRequestType(0, "", 0);
      store.dispatch("common/stopLoading");
    };

    // フォルダのレシピ一覧を初期化して取得
    const initializeAndGetRecipeList = async () => {
      NativeClient.sendDebugLog("MypageFolderPage initializeAndGetRecipeList");
      totalCount = 0;
      recipeCount = 0;
      // マイリスト初期化
      if (isRecentList) {
        await store.dispatch("mypage/updateFavoriteRecipeList", null);
      } else {
        await store.dispatch("mypage/updateFolderRecipeList", null);
      }
      getRecipeList();
    };

    // フォルダ削除
    const completeDeleteFolderHandler = () => {
      //フォルダが削除されたら戻る
      router.go(-1);
    };
    const deleteFolder = async () => {
      NativeClient.sendDebugLog("MypageFolderPage deleteFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFolderPageRequestType(2, "", 0);
        await store.dispatch("mypage/deleteFolder", currentId);
        NativeClient.sendDebugLog("MypageFolderPage deleteFolder end");

        // 削除完了ダイアログ表示
        showCompleteOperationDialog("", dialogMessageCompleteFolderDelete, completeDeleteFolderHandler);
      }
      store.dispatch("common/stopLoading");
    };

    // フォルダ名変更
    const renameFolder = async (folderName: string) => {
      NativeClient.sendDebugLog("MypageFolderPage renameFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFolderPageRequestType(3, folderName, 0);
        await store.dispatch("mypage/renameFolder", { folderId: currentId, folderName: folderName });
        NativeClient.sendDebugLog("MypageFolderPage renameFolder end folderName = " + folderName);
        // 変更完了
        currentFolderName = folderName;
        refCurrentFolderName.value = folderName;
        store.dispatch("mypage/updateFolderName", folderName);
        NativeClient.setTitle(new TitleInfo(false, currentFolderName));
      }
      store.dispatch("common/stopLoading");
    };

    // レシピ削除
    const completeDeleteRecipeHandler = () => {
      // 再度、レシピ一覧を取得する
      clearList();
      getRecipeList();
    };
    const deleteRecipe = async (recipeId: number) => {
      NativeClient.sendDebugLog("MypageFolderPage deleteFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFolderPageRequestType(2, "", 0);
        if (isRecentList) {
          await store.dispatch("mypage/deleteRecipe", recipeId);
        } else {
          await store.dispatch("mypage/deleteFolderRecipe",  { folderId: currentId, recipeIds: [recipeId] });
        }
        NativeClient.sendDebugLog("MypageFolderPage deleteFolder end");

        // 削除完了ダイアログ表示
        showCompleteOperationDialog("", dialogMessageForCompleteRecipeDelete, completeDeleteRecipeHandler);
      }
      store.dispatch("common/stopLoading");

      // Nativeへ通知
      if (isRecentList) {
        NativeClient.requestReloadMylist();
      }
    };

    // 実行
    const execute = async () => {
      NativeClient.sendDebugLog("MypageFolderPage execute()");
      getRecipeList();
    };

    // Nativeからの更新要求
    const updatePage = (info: UpdatePageInfo) => {
      NativeClient.sendDebugLog("MypageFolderPage updatePage()");
      if (info.myList) {
        NativeClient.sendDebugLog("MypageFolderPage updatePage() update mylist");
        initializeAndGetRecipeList();
      }
    };

    // 表示状態の制御
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;

      // フォルダー・レシピ一覧を初期化
      NativeClient.registUpdatePageListener(updatePage);

      execute();

      // Beacon Log
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST_RECENT_LIST, null, []);
      if (!isRecentList) {
        logInfo.pageId = PAGE_ID.FAVORITES_MYLIST_FOLDER;
      }
      NativeClient.sendViewedLog(logInfo)
    });
    onUnmounted(() => {
      isDisplayed = false;
      clearList();
    });

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("MypageFolderPage onScroll totalCount = " + totalCount + "recipeCount = " + recipeCount);
      if (
        isEndOfScrollBottom(event) &&
        (currentRequest.type == 0) &&
        (recipeCount < totalCount)
      ) {
        NativeClient.sendDebugLog("MypageFolderPage onScroll detect");
        currentRequest = new MypageFolderPageRequestType(5, "", 0);
        getRecipeList();
      }
    };

    // レシピ一覧の部品のインターフェースに合わせて、配列を生成する
    const recipeList = computed(() => {
      let clips: Array<MypageRecipeClip> = [];
      NativeClient.sendDebugLog("MypageFolderPage recipeList");
      if (isRecentList) {
        const recipeList: MypageRecipeClips = store.getters["mypage/getFavoriteRecipeList"];
        NativeClient.sendDebugLog("MypageFolderPage recipeList recentList");
        if (recipeList != null) {
          clips = recipeList.clips;
        } else {
          NativeClient.sendDebugLog("MypageFolderPage recipeList recentList is Null");
          return null;
        }
      } else {
        const folderRecipeList: MypageRecipeClips = store.getters["mypage/getFolderRecipeList"];
        NativeClient.sendDebugLog("MypageFolderPage recipeList folderRecipeList");
        if (folderRecipeList != null) {
          clips = folderRecipeList.clips;
        } else {
          NativeClient.sendDebugLog("MypageFolderPage recipeList folderRecipeList is Null");
          return null;
        }
      }
      const recipeList = createFavoriteRecipesToCommonRecipes(clips);
      return recipeList;
    });

    // レシピ追加ボタンの表示制御
    const styleFolderOperation = computed(() => {
      if (isRecentList) {
        // 全てのマイリスト
        return { display: "none" };
      }
      return { display: "flex" };
    });
    const styleBodySpacer = computed(() => {
      if (isRecentList) {
        return { display: "none" };
      } else {
        return  { display: "inline-block" };
      }
    });

    // フォルダ名変更ダイアログ
    const isShowRenameFolderDialog = ref(false);
    const showRenameFolderDialog = () => {
      isShowRenameFolderDialog.value = true;
      const logInfo = new BeaconLogInfo(PAGE_ID.DIALOG, null, ["お気に入りフォルダ変更"]);
      NativeClient.sendViewedLog(logInfo);
    };
    const folderName = computed(() => {
      return refCurrentFolderName.value;
    });
    const closeRenameFolderDialogHandler = () => {
      // ダイアログを閉じる
      isShowRenameFolderDialog.value = false;
    };
    const renameFolderHandler = (folderName: string) => {
      isShowRenameFolderDialog.value = false;
      if (folderName.length > COMMON_LIMIT_NUM.FAVORITES_FOLDER_NAME_LIMIT) {
        showOperationErrorDialog("", dialogMessageForLimitOverFolderName);
        return;
      }
      renameFolder(folderName);
    };

    // フォルダ操作選択のダイアログ
    const isShowFolderOperation = ref(false);
    const folderOperationAction = () => {
      NativeClient.sendDebugLog("MypageFolderPage operation");
      isShowFolderOperation.value = true;
    };
    const folderOperationHandler = (selectNumber: number) => {
      NativeClient.sendDebugLog("MypageFolderPage select = " + selectNumber);
      isShowFolderOperation.value = false;
      switch (selectNumber) {
        case 0:
          // フォルダ削除確認ダイアログ
          showConfirmOperationDialog("", folderDeleteMessage, deleteFolder)
          break;
        case 1:
          // フォルダ名変更
          updateInputFolderName();
          showRenameFolderDialog();
          break;
        default:
          break;
      }
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const okApiErrorHandler = () => {
      closeDialog();
      currentRequest = new MypageFolderPageRequestType(0, "", 0);
    };
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // switch (currentRequest.type) {
      //   case 1:
      //     getRecipeList();
      //     break;
      //   case 2:
      //     deleteFolder();
      //     break;
      //   case 3:
      //     renameFolder(currentRequest.folderName);
      //     break;
      //   case 4:
      //     deleteRecipe(currentRequest.recipeId);
      //     break;
      //   default:
      //     break;
      // }
    };

    // 追加ボタン
    const appendFolderAction = () => {
      NativeClient.sendDebugLog("MypageFolderPage Append");
      if (totalCount >= 99) {
        showOperationErrorDialog("", dialogMessageForRecipeLimitOver);
      } else {
        router.push(`/mylist-select?folderId=` + currentId);
      }
    };

    // レシピ選択
    const selectRecipeHandler = (recipeId: number, type: string) => {
      NativeClient.sendDebugLog("MypageFolderPage selectRecipe");
      NativeClient.transitionReceipDetail(recipeId, type);
    };
    // レシピ削除
    let deleteTargetRecipeId = 0;
    const deleteRecipeDialogHandler = () => {
      deleteRecipe(deleteTargetRecipeId);
    }
    const deleteRecipeHandler = (recipeId: number) => {
      NativeClient.sendDebugLog("MypageFolderPage delete Recipe");
      deleteTargetRecipeId = recipeId;
      showConfirmOperationDialog("", recipeDeleteMessage, deleteRecipeDialogHandler);
    };

    return {
      onScroll,
      recipeList,
      deleteMode,
      selectRecipeHandler,
      deleteRecipeHandler,
      styleFolderOperation,
      styleBodySpacer,
      appendFolderAction,
      folderOperationAction,
      operationAppendText,
      isShowDialog,
      generalDialogInfo,
      closeGeneralDialog,
      positiveHandleGeneralDialog,
      negativeHandleGeneralDialog,
      okText,
      isShowFolderOperation,
      folderOperationHandler,
      isShowRenameFolderDialog,
      closeRenameFolderDialogHandler,
      folderName,
      renameFolderHandler,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      okApiErrorHandler,
      retryApiErrorHandler,
      updateDisplayKey,
      updateInputFolderNameKey
    };
  }
});
